import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { formatDisplayDate } from "../../../../common/utils/date";
import MoreOptionsPopover from "../common/MoreOptionsPopover";
import MoreIcon from "../../svg/MoreIcon";
import { isLabPanelEditable } from "../../utils/labPanelStatus";
import {
  deleteCustomerPrescription,
  deleteCustomerPrescriptionItem,
  updateCustomerPrescription,
} from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";
import { getAnswerSaveErrorSnackbar } from "../../utils/labPanelQuestions";
import { useState } from "react";
import { useClientSidePanel } from "../../../../providers/ClientSidePanelProvider";
import EditServiceModal from "../logicBuilder/EditServiceModal";
import EditLabPanelModal from "../logicBuilder/EditLabPanelModal";
import PrescriptionTypeBadge from "./PrescriptionTypeBadge";
import PrescriptionStatusBadge from "./PrescriptionStatusBadge";
import PrescriptionStatusSelect from "./PrescriptionStatusSelect";
import PrescriptionServiceCard from "./PrescriptionServiceCard";
import PrescriptionLabPanelCard from "./PrescriptionLabPanelCard";
import ClientBadge from "../common/ClientBadge";

interface Service {
  name: string;
  [key: string]: any;
}

interface LabPanel {
  id?: string;
  userLabPanelID?: string;
  name?: string;
  provider?: string;
  kind?: number;
  startDate?: string;
  startOffset?: number;
  startOffsetUnit?: number;
  items?: { biomarkerid: string; providerbiomarkerid: string }[];
  logicName?: string;
  logicBuilderID?: string;
  system?: number;
  isExpired?: boolean;
  status?: number;
  category?: number;
  serviceType?: number;
  active?: number;
  isActive?: boolean;
}

/**
 * What the viewer may do to the record.
 *
 * - `workspace` — the client workspace: everything, including Regenerate and
 *   "Add new service/panel", which open the ClientSidePanel and act on
 *   `ASSelectedClient`.
 * - `record` — History's cross-client page: the actions that only need the
 *   record itself (delete the prescription, toggle its status, edit or delete
 *   a service or panel). They key off `prescription.customerID`, since no
 *   client is selected there. Regenerate and adding items need the workspace.
 * - `none` — a customer reading their own history.
 */
export type PrescriptionActionScope = "workspace" | "record" | "none";

interface PrescriptionDetailsViewProps {
  prescription: {
    id: string;
    customerID?: string;
    dateCreated: string;
    active: number;
    isActive: boolean;
    isExpired: boolean;
    sourceType: number;
    services: Service[];
    labPanels?: LabPanel[];
  };
  /**
   * Return to the listing. Not a header affordance — the breadcrumb carries
   * the back link (`useBackNavigation`) — but a deleted prescription's detail
   * must not stay open, so the delete path calls this.
   */
  onBack: () => void;
  onRegenerate: () => void;
  onDelete: () => Promise<void>;
  isLatestAutoGenerated?: boolean;
  setLoading: (loading: boolean) => void;
  /** Defaults to the full workspace set. */
  actions?: PrescriptionActionScope;
  /**
   * Names the client in the header (`ClientBadge`). Only the cross-client
   * History page passes it — the workspace shells already name the subject in
   * their top bar.
   */
  clientName?: string | null;
}

const PrescriptionDetailsView = ({
  prescription,
  onBack,
  onRegenerate,
  onDelete,
  isLatestAutoGenerated = false,
  setLoading,
  actions = "workspace",
  clientName,
}: PrescriptionDetailsViewProps) => {
  const readOnly = actions === "none";
  const inWorkspace = actions === "workspace";
  const theme: Theme = useTheme();
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLabPanel, setEditingLabPanel] = useState<LabPanel | null>(null);
  const [isEditLabPanelModalOpen, setIsEditLabPanelModalOpen] = useState(false);
  const {
    ASSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();
  const { setEditingPrescription, openPanel } = useClientSidePanel();
  // The client this record belongs to. The record says so itself; the
  // selected client is only a fallback for older payloads without the field.
  const customerID = prescription.customerID || ASSelectedClient?.id || "";

  // Handle edit prescription
  const handleEdit = () => {
    setEditingPrescription(prescription);
    openPanel();
  };

  // Handle edit service
  const handleEditService = (service: Service) => {
    setEditingService(service);
    setIsEditModalOpen(true);
  };

  // Handle save service
  const handleSaveService = async (updatedService: Service) => {
    try {
      // Update the service in the prescription
      const updatedServices = prescription.services.map((s) =>
        s.id === updatedService.id ? updatedService : s
      );

      await updateCustomerPrescription({
        ...prescription,
        services: updatedServices,
      });

      // Show success message
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Success",
        message: "Service updated successfully!",
      });
      ASSetTypeSnackBar("success");

      // Call onDelete callback to refetch data
      if (onDelete) {
        await onDelete();
      }

      setIsEditModalOpen(false);
      setEditingService(null);
    } catch (error) {
      console.error("Error updating service:", error);

      // Show error message
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update service. Please try again.",
      });
      ASSetTypeSnackBar("alert");
    }
  };

  // Handle status change
  const handleStatusChange = async (isActive: boolean) => {
    setLoading(true);
    try {
      await updateCustomerPrescription({
        ...prescription,
        active: prescription.active === 0 ? 1 : 0,
        isActive: prescription.active === 0,
      });

      // Show success message
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Success",
        message: `Prescription ${
          isActive ? "activated" : "deactivated"
        } successfully!`,
      });
      ASSetTypeSnackBar("success");

      // Refetch prescription data
      if (onDelete) {
        await onDelete();
      }
    } catch (error) {
      console.error("Error updating prescription status:", error);

      // Show error message
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update prescription status. Please try again.",
      });
      ASSetTypeSnackBar("alert");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete prescription
  const handleDelete = () => {
    // Panels past the authorize/payment-failed stage can't be deleted — the
    // backend keeps them and removes only the services and the still-deletable
    // panels.
    const labPanels = prescription.labPanels || [];
    const lockedPanelCount = labPanels.filter(
      (lp) => !isLabPanelEditable(lp)
    ).length;
    // Everything the delete would actually remove: services + deletable panels.
    const removableCount =
      (prescription.services?.length || 0) +
      (labPanels.length - lockedPanelCount);

    // Nothing is removable and some panels are locked → deleting would do
    // nothing. Show an info alert (OK only, no confirm) instead of the
    // delete confirmation.
    if (lockedPanelCount > 0 && removableCount === 0) {
      ASSetSnackBarMessage({
        title: "Can't delete prescription",
        message: `This prescription only contains lab panel${
          lockedPanelCount > 1 ? "s" : ""
        } whose status prevents removal, so there's nothing to delete. ${
          lockedPanelCount > 1 ? "They" : "It"
        } will remain in this prescription.`,
      });
      ASSetTypeSnackBar("alert");
      ASSetOpenSnackBar(true);
      return;
    }

    const deleteMessage =
      lockedPanelCount > 0
        ? `${lockedPanelCount} lab panel${
            lockedPanelCount > 1 ? "s" : ""
          } can't be deleted because of their status and will remain in this prescription. The rest of the items in the prescription will be removed. This action cannot be undone.`
        : "Are you sure you want to delete this prescription? This action cannot be undone.";

    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete prescription",
      message: deleteMessage,
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        if (!prescription.id) {
          console.error("Prescription ID is missing");
          return;
        }

        try {
          await deleteCustomerPrescription(prescription.id);

          // Show success message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: "Prescription deleted successfully!",
          });
          ASSetTypeSnackBar("success");

          // Call onDelete callback to refetch data
          if (onDelete) {
            await onDelete();
          }
          onBack();
        } catch (error) {
          console.error("Error deleting prescription:", error);

          // Show error message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete prescription. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  // Handle delete service
  const handleDeleteService = (service: Service) => {
    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete service",
      message: `Are you sure you want to delete "${service.name}" from this prescription? This action cannot be undone.`,
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        if (!prescription.id) {
          console.error("Prescription ID is missing");
          return;
        }

        try {
          // Delete just this item via the per-item endpoint (no more PUTting
          // the whole prescription minus the service).
          await deleteCustomerPrescriptionItem(prescription.id, service.id);

          // Show success message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: `Service "${service.name}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");

          // Close the edit modal and refetch
          setIsEditModalOpen(false);
          setEditingService(null);
          if (onDelete) {
            await onDelete();
          }
        } catch (error) {
          console.error("Error deleting service:", error);

          // Show error message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete service. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  // Handle delete lab panel
  const handleDeleteLabPanel = (labPanel: LabPanel) => {
    const panelName = labPanel.name || "Lab panel";
    const itemId = labPanel.id;

    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete lab panel",
      message: `Are you sure you want to delete "${panelName}" from this prescription? This action cannot be undone.`,
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        if (!prescription.id || !itemId) {
          console.error("Prescription ID or lab panel ID is missing");
          return;
        }

        try {
          await deleteCustomerPrescriptionItem(prescription.id, itemId);

          // Show success message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: `Lab panel "${panelName}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");

          // Close the edit modal and refetch
          setIsEditLabPanelModalOpen(false);
          setEditingLabPanel(null);
          if (onDelete) {
            await onDelete();
          }
        } catch (error: any) {
          console.error("Error deleting lab panel:", error);

          // 422 = backend rejected because the panel's status (paid/submitted)
          // does not allow deletion. Surface the specific reason.
          const isStatusLocked = error?.response?.status === 422;
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: isStatusLocked ? "Can't delete lab panel" : "Error",
            message: isStatusLocked
              ? `"${panelName}" can't be deleted because its status no longer allows removal.`
              : "Failed to delete lab panel. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  // Handle edit lab panel
  const handleEditLabPanel = (labPanel: LabPanel) => {
    setEditingLabPanel(labPanel);
    setIsEditLabPanelModalOpen(true);
  };

  // Handle save lab panel
  const handleSaveLabPanel = async (updatedLabPanel: LabPanel) => {
    try {
      const updatedLabPanels = (prescription.labPanels || []).map(
        (lp: LabPanel) => {
          const lpId = lp.userLabPanelID || lp.id;
          const updatedId =
            updatedLabPanel.userLabPanelID || updatedLabPanel.id;
          return lpId === updatedId ? updatedLabPanel : lp;
        }
      );

      await updateCustomerPrescription({
        ...prescription,
        labPanels: updatedLabPanels,
      });

      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Success",
        message: "Lab panel updated successfully!",
      });
      ASSetTypeSnackBar("success");

      if (onDelete) {
        await onDelete();
      }

      setIsEditLabPanelModalOpen(false);
      setEditingLabPanel(null);
    } catch (error: any) {
      console.error("Error updating lab panel:", error);

      // A 422 on the lab's order-entry answers lists the per-question
      // reasons (same as the prescription builder).
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage(
        getAnswerSaveErrorSnackbar(
          error,
          "Failed to update lab panel. Please try again."
        )
      );
      ASSetTypeSnackBar("alert");
      // Keep the modal open with the typed answers so they can be corrected.
      throw error;
    }
  };

  // Create menu options for prescription header
  const createPrescriptionMenuOptions = () => {
    const options = [
      {
        label: "Delete prescription",
        onClick: handleDelete,
      },
    ];

    // Adding items opens the ClientSidePanel and Regenerate acts on the
    // selected client — both exist only in the client workspace.
    if (inWorkspace) {
      options.push({
        label: "Add new service/panel",
        onClick: handleEdit,
      });
      // Regenerate only for the most current auto-generated prescription
      if (prescription.sourceType === 1 && isLatestAutoGenerated) {
        options.push({
          label: "Regenerate",
          onClick: onRegenerate,
        });
      }
    }

    return options;
  };

  return (
    <div style={styles.container}>
      {/* No title or back button: the breadcrumb names the page and carries
          the link back to the listing. The status row below already
          identifies the prescription. */}
      <div style={styles.contentContainerHeader(theme)}>
        <div style={styles.badgesContainer}>
          {prescription.isExpired ? (
            <div style={styles.expireContainer(theme)}>Expired</div>
          ) : readOnly ? (
            <PrescriptionStatusBadge
              isActive={prescription.active === 1}
              isExpired={false}
            />
          ) : (
            <PrescriptionStatusSelect
              isActive={prescription.active === 1}
              onStatusChange={handleStatusChange}
            />
          )}
          <PrescriptionTypeBadge sourceType={prescription.sourceType} />
          <div style={styles.dateHeader(theme)}>
            {`Created ${formatDisplayDate(prescription.dateCreated)}`}
          </div>
        </div>
        <div style={styles.headerRight}>
          <ClientBadge name={clientName} />
          {!readOnly && (
            <MoreOptionsPopover
              minWidth={"200px"}
              options={createPrescriptionMenuOptions()}
              icon={<MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div style={styles.contentWithScroll}>
        <ScrollableWrapper customPadding="0px">
          <div style={styles.contentContainer()}>
            {/* Lab Panels Section */}
            {prescription.labPanels && prescription.labPanels.length > 0 && (
              <div style={styles.serviceContainer}>
                {prescription.labPanels.map(
                  (labPanel: LabPanel, index: number) => (
                    <PrescriptionLabPanelCard
                      key={labPanel.userLabPanelID || labPanel.id || index}
                      labPanel={labPanel}
                      onEditLabPanel={handleEditLabPanel}
                      customerID={customerID}
                      onRefetch={onDelete}
                      readOnly={readOnly}
                    />
                  )
                )}
              </div>
            )}

            {/* Services Section */}
            {prescription.services && prescription.services.length > 0 && (
              <div style={styles.serviceContainer}>
                {prescription.services.map(
                  (service: Service, index: number) => (
                    <PrescriptionServiceCard
                      key={service.name || index}
                      service={service}
                      index={index}
                      onEditService={handleEditService}
                      readOnly={readOnly}
                    />
                  )
                )}
              </div>
            )}

            {/* Empty state */}
            {!prescription.labPanels?.length &&
              !prescription.services?.length && (
                <div style={styles.emptyState(theme)}>
                  No services or lab panels found
                </div>
              )}
          </div>
        </ScrollableWrapper>
      </div>

      {/* Edit Service Modal - for both auto-generated and manual prescriptions */}
      {editingService && (
        <EditServiceModal
          open={isEditModalOpen}
          service={editingService}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingService(null);
          }}
          onSave={handleSaveService}
          onDelete={handleDeleteService}
          canDelete={prescription.services.length > 1}
          type={"prescriptionBuilder"}
        />
      )}

      {/* Edit Lab Panel Modal */}
      <EditLabPanelModal
        open={isEditLabPanelModalOpen}
        labPanel={editingLabPanel}
        onClose={() => {
          setIsEditLabPanelModalOpen(false);
          setEditingLabPanel(null);
        }}
        onSave={handleSaveLabPanel}
        onDelete={handleDeleteLabPanel}
        canDelete={
          editingLabPanel ? isLabPanelEditable(editingLabPanel) : false
        }
        type="prescriptionBuilder"
      />
    </div>
  );
};

export default PrescriptionDetailsView;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
  },
  contentWithScroll: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    marginRight: "-8px",
  },
  contentContainer: () => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  }),
  contentContainerHeader: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    marginBottom: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  badgesContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },
  headerRight: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },
  dateHeader: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  serviceContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  emptyState: (theme: Theme) => ({
    padding: "24px",
    textAlign: "center" as "center",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
  expireContainer: (theme: Theme) => ({
    padding: "4px 12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    background: theme.colorTokens.D[10],
    border: `1px solid ${theme.colorTokens.D[70]}`,
    borderRadius: "8px",
  }),
};
